import { useState } from 'react';

interface Step {
  title: string;
  fields: readonly string[];
}

interface StepFormConfig {
  steps: Record<number, Step>;
  totalSteps: number;
}

interface UseStepFormReturn<T> {
  formData: T;
  step: number;
  currentFields: readonly string[];
  isLastStep: boolean;
  progress: number;
  currentStepTitle: string;
  handleInputChange: (e: { target: { name: string; value: string } }) => void;
  handleValueChange: (key: keyof T, value: string) => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (stepNumber: number) => void;
}

export function useStepForm<T extends object>(
  config: StepFormConfig,
  initialData: T
): UseStepFormReturn<T> {
  const [formData, setFormData] = useState<T>(initialData);
  const [step, setStep] = useState(1);

  const handleInputChange = (e: { target: { name: string; value: string } }): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleValueChange = (key: keyof T, value: string): void => {
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const nextStep = (): void => {
    if (step < config.totalSteps) {
      setStep(prev => prev + 1);
    }
  };

  const previousStep = (): void => {
    if (step > 1) {
      setStep(prev => prev - 1);
    }
  };

  const goToStep = (stepNumber: number): void => {
    if (stepNumber >= 1 && stepNumber <= config.totalSteps) {
      setStep(stepNumber);
    }
  };

  return {
    formData,
    step,
    currentFields: config.steps[step].fields,
    isLastStep: step === config.totalSteps,
    progress: (step / config.totalSteps) * 100,
    currentStepTitle: config.steps[step].title,
    handleInputChange,
    handleValueChange,
    nextStep,
    previousStep,
    goToStep,
  };
}
